import React, { useState } from 'react';
import { Box, Button, Container, Grid, IconButton, Link as MuiLink, Stack, Typography } from '@mui/material';
import {
  ArrowForward,
  Close,
  Facebook,
  Instagram,
  InsertChartOutlined,
  KeyboardArrowDown,
  Payments,
  PeopleAltOutlined,
  Route,
  Savings,
} from '@mui/icons-material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ProviderSavingsCalculator from '../components/ProviderSavingsCalculator';
import SEO from '../components/SEO';
import {
  ACCENT,
  ClosingCta,
  DEEP,
  Eyebrow,
  GREEN,
  HERO_IMAGE,
  INK,
  LINE,
  LINE_ON_DARK,
  Lede,
  MINT,
  MUTED,
  MarketingPage,
  SectionTitle,
  SplitHeading,
  primaryButtonSx,
  secondaryButtonSx,
} from '../components/marketing/MarketingKit';

const HIDE_NUDGE_KEY = 'lh_hide_savings_nudge';

const AUDIENCES: Array<{ eyebrow: string; title: string; to: string }> = [
  { eyebrow: 'For lawn care businesses', title: 'Run the business, not the paperwork.', to: '/provider-benefits' },
  { eyebrow: 'For homeowners', title: 'Hire lawn care without the phone tag.', to: '/customer-benefits' },
];

const PROVIDER_HIGHLIGHTS: Array<{ icon: React.ReactNode; title: string; body: string }> = [
  {
    icon: <PeopleAltOutlined fontSize="small" />,
    title: 'Customers & agreements',
    body: 'Bring your existing customers over and get agreements e-signed.',
  },
  {
    icon: <Route fontSize="small" />,
    title: 'Routes & crew app',
    body: 'Optimized routes for up to 6 crews, with stops and photos from any phone.',
  },
  {
    icon: <Payments fontSize="small" />,
    title: 'Payments',
    body: 'Cards are charged when the job is done, with weekly payouts.',
  },
  {
    icon: <InsertChartOutlined fontSize="small" />,
    title: 'Financials',
    body: 'Revenue, expenses, a booked forecast, and profit per property.',
  },
];

const PROVIDER_STEPS: Array<{ title: string; body: string }> = [
  {
    title: 'Create your free account',
    body: 'Set your services and service area. There’s no subscription to start running your book.',
  },
  {
    title: 'Onboard customers or win leads',
    body: 'Bring your own customers at 1%, or bid on marketplace requests when you want to grow.',
  },
  {
    title: 'Schedule, crew, and get paid',
    body: 'Run customers, routes, crews, and payments in one place — with payouts every week.',
  },
];

const FEES: Array<{ value: string; label: string }> = [
  { value: '1%', label: 'on customers you bring' },
  { value: '5%', label: 'on new marketplace customers, first season' },
  { value: '$0', label: 'monthly subscription' },
];

const FOOTER_COLUMNS: Array<{ title: string; links: Array<{ label: string; to: string }> }> = [
  {
    title: 'LawnHub',
    links: [
      { label: 'For providers', to: '/provider-benefits' },
      { label: 'For homeowners', to: '/customer-benefits' },
      { label: 'How it works', to: '/how-it-works' },
      { label: 'Request a demo', to: '/request-demo' },
      { label: 'About us', to: '/about-us' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { label: 'FAQs', to: '/faqs' },
      { label: 'Lawn care scheduling software', to: '/lawn-care-scheduling-software' },
      { label: 'Lawn care CRM', to: '/lawn-care-crm' },
      { label: 'Jobber alternative', to: '/jobber-alternative' },
      { label: 'Legal (agreements)', to: '/legal' },
      { label: 'Privacy Policy', to: '/privacy-policy' },
    ],
  },
  {
    title: 'Account',
    links: [
      { label: 'Sign up free', to: '/get-started' },
      { label: 'Log in', to: '/login' },
    ],
  },
];

/* ---------- Page ---------- */

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [showSavingsNudge, setShowSavingsNudge] = useState(() => {
    if (typeof window === 'undefined') return true;
    return sessionStorage.getItem(HIDE_NUDGE_KEY) !== '1';
  });

  const scrollToProviderSavings = () => {
    document.getElementById('provider-savings-snapshot')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const dismissSavingsNudge = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setShowSavingsNudge(false);
    sessionStorage.setItem(HIDE_NUDGE_KEY, '1');
  };

  const handleGetStarted = () => {
    if (user) {
      navigate(user.userType === 'customer' ? '/customer' : '/provider');
    } else {
      navigate('/get-started');
    }
  };

  return (
    <>
      <SEO
        title="LawnHub — Lawn Care Business Platform"
        description="All-in-one platform for lawn care and snow removal businesses. Schedule jobs, manage crews and customers, and get paid — nationwide. No forced monthly subscription."
        keywords="lawn care business software, lawn care CRM, lawn care scheduling software, snow removal business software, landscaping software, crew management, Jobber alternative, lawn care route optimization"
        url="/"
        type="website"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is LawnHub?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'LawnHub is an all-in-one business platform for lawn care and snow removal companies. Schedule jobs, manage crews and customers, run routes, track finances, and get paid — without forced monthly subscriptions. Businesses across the country can use the platform.',
              },
            },
            {
              '@type': 'Question',
              name: 'How much does LawnHub cost for providers?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'There is no forced monthly subscription. Platform fees are typically 1% when you bring your own customers onto LawnHub, and 5% for new marketplace leads in the first season (1% on renewals with the same provider). Customers pay card processing separately.',
              },
            },
            {
              '@type': 'Question',
              name: 'Can I bring my existing lawn care customers onto LawnHub?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. You can onboard your existing customer base and keep those relationships while using CRM, scheduling, crew tools, and automated payments — usually at a 1% platform fee on completed jobs.',
              },
            },
            {
              '@type': 'Question',
              name: 'Does LawnHub replace tools like Jobber or spreadsheets?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'LawnHub is built as a complete ops platform: CRM, scheduling, route optimization, mobile crew app, payments, Financial Dashboard, and optional crew time tracking with payroll-ready export — so many providers can replace spreadsheet chaos or expensive subscription software.',
              },
            },
            {
              '@type': 'Question',
              name: 'Can homeowners use LawnHub too?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes. Homeowners can request lawn care and snow removal, compare bids from verified marketplace providers, manage services for family properties, and pay securely online.',
              },
            },
          ],
        }}
      />

      <MarketingPage>
        {/* Hero */}
        <Box
          component="section"
          sx={{
            position: 'relative',
            color: '#fff',
            textAlign: 'center',
            minHeight: { xs: 620, md: 700 },
            display: 'flex',
            flexDirection: 'column',
            backgroundImage: `linear-gradient(180deg, rgba(12, 34, 18, 0.72) 0%, rgba(20, 60, 28, 0.36) 40%, rgba(12, 34, 18, 0.8) 100%), url("${HERO_IMAGE}")`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <Container
            maxWidth="md"
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              pt: { xs: 11, md: 14 },
              pb: { xs: 6, md: 8 },
            }}
          >
            <Box
              component="img"
              src="/hero/lawnhub-logo-circle.png"
              alt="LawnHub"
              sx={{
                width: { xs: 132, sm: 160, md: 184 },
                height: 'auto',
                mb: { xs: 3, md: 3.5 },
                filter: 'drop-shadow(0 12px 30px rgba(0,0,0,0.35))',
              }}
            />
            <Typography
              component="h1"
              sx={{
                fontWeight: 800,
                letterSpacing: '-0.03em',
                lineHeight: 1.08,
                fontSize: { xs: '2.1rem', sm: '2.8rem', md: '3.5rem' },
                textShadow: '0 2px 24px rgba(0,0,0,0.35)',
                maxWidth: 780,
              }}
            >
              The complete lawn care business platform.
            </Typography>
            <Typography
              sx={{
                mt: 2.5,
                maxWidth: 600,
                fontSize: { xs: '1.02rem', md: '1.15rem' },
                lineHeight: 1.6,
                color: 'rgba(255,255,255,0.9)',
                textShadow: '0 1px 12px rgba(0,0,0,0.35)',
              }}
            >
              Schedule jobs, manage crews and customers, and get paid — without the spreadsheet chaos or a forced monthly
              subscription.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 4, width: { xs: '100%', sm: 'auto' } }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleGetStarted}
                endIcon={<ArrowForward />}
                sx={{ ...primaryButtonSx, bgcolor: ACCENT, '&:hover': { bgcolor: '#f57c00', boxShadow: 'none' } }}
              >
                Get started free
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={scrollToProviderSavings}
                startIcon={<Savings />}
                sx={{ ...secondaryButtonSx, color: DEEP, borderColor: '#fff', '&:hover': { bgcolor: MINT, borderColor: MINT } }}
              >
                See your savings
              </Button>
            </Stack>
            <Typography sx={{ mt: 2.5, fontSize: '0.92rem', color: 'rgba(255,255,255,0.8)' }}>
              <MuiLink
                component={RouterLink}
                to="/request-demo"
                sx={{ color: '#fff', fontWeight: 600, textDecorationColor: 'rgba(255,255,255,0.5)' }}
              >
                Request an interactive demo
              </MuiLink>
              <Box component="span" sx={{ mx: 1.25, color: 'rgba(255,255,255,0.4)' }}>
                |
              </Box>
              Already have an account?{' '}
              <MuiLink component={RouterLink} to="/login" sx={{ color: '#fff', fontWeight: 600, textDecorationColor: 'rgba(255,255,255,0.5)' }}>
                Sign in
              </MuiLink>
            </Typography>
          </Container>

          {showSavingsNudge && (
            <Box
              role="button"
              tabIndex={0}
              onClick={scrollToProviderSavings}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  scrollToProviderSavings();
                }
              }}
              sx={{
                cursor: 'pointer',
                bgcolor: 'rgba(10, 28, 15, 0.55)',
                backdropFilter: 'blur(6px)',
                borderTop: '1px solid rgba(255,255,255,0.14)',
                py: 1.1,
                px: { xs: 1, sm: 2 },
                '&:hover .lh-nudge-cta': { textDecoration: 'underline' },
                '@keyframes lhNudgePulse': {
                  '0%, 100%': { transform: 'scale(1)' },
                  '50%': { transform: 'scale(1.12)' },
                },
                '@keyframes lhNudgeBounce': {
                  '0%, 100%': { transform: 'translateY(0)' },
                  '50%': { transform: 'translateY(3px)' },
                },
              }}
            >
              <Container maxWidth="lg" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: { xs: 1, sm: 1.5 } }}>
                <Savings sx={{ fontSize: 20, color: ACCENT, flexShrink: 0, animation: 'lhNudgePulse 1.6s ease-in-out infinite' }} />
                <Typography sx={{ fontSize: { xs: '0.8rem', sm: '0.9rem' }, lineHeight: 1.35, textAlign: 'center' }}>
                  Psst… how much could your lawn business save this year?{' '}
                  <Box component="span" className="lh-nudge-cta" sx={{ fontWeight: 700, color: '#cfe6d2', whiteSpace: 'nowrap' }}>
                    Try the Savings Snapshot
                  </Box>
                </Typography>
                <KeyboardArrowDown
                  sx={{
                    fontSize: 20,
                    color: '#cfe6d2',
                    flexShrink: 0,
                    display: { xs: 'none', sm: 'block' },
                    animation: 'lhNudgeBounce 1.1s ease-in-out infinite',
                  }}
                />
                <IconButton
                  aria-label="Dismiss savings tip"
                  size="small"
                  onClick={dismissSavingsNudge}
                  sx={{ ml: { xs: 0, sm: 1 }, color: 'rgba(255,255,255,0.6)', flexShrink: 0, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.08)' } }}
                >
                  <Close fontSize="small" />
                </IconButton>
              </Container>
            </Box>
          )}
        </Box>

        {/* Two audiences */}
        <Box component="section" sx={{ bgcolor: '#fff', borderBottom: `1px solid ${LINE}` }}>
          <Container maxWidth="lg">
            <Grid container>
              {AUDIENCES.map((a, i) => (
                <Grid item xs={12} md={6} key={a.to}>
                  <Box
                    component={RouterLink}
                    to={a.to}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 3,
                      height: '100%',
                      py: { xs: 3.5, md: 5 },
                      pl: { md: i === 1 ? 5 : 0 },
                      pr: { md: i === 0 ? 5 : 0 },
                      borderLeft: { md: i === 1 ? `1px solid ${LINE}` : 'none' },
                      borderTop: { xs: i === 1 ? `1px solid ${LINE}` : 'none', md: 'none' },
                      color: INK,
                      textDecoration: 'none',
                      '&:hover .lh-arrow': { transform: 'translateX(4px)', bgcolor: GREEN, borderColor: GREEN, color: '#fff' },
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: GREEN, mb: 1 }}>
                        {a.eyebrow}
                      </Typography>
                      <Typography component="h2" sx={{ fontWeight: 800, fontSize: { xs: '1.3rem', md: '1.55rem' }, letterSpacing: '-0.02em', lineHeight: 1.2 }}>
                        {a.title}
                      </Typography>
                    </Box>
                    <Box
                      className="lh-arrow"
                      sx={{
                        width: 44,
                        height: 44,
                        flexShrink: 0,
                        borderRadius: '50%',
                        border: `1px solid ${LINE}`,
                        display: 'grid',
                        placeItems: 'center',
                        color: GREEN,
                        transition: 'all 160ms ease',
                      }}
                    >
                      <ArrowForward fontSize="small" />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Provider highlights */}
        <Box component="section" sx={{ py: { xs: 7, md: 10 } }}>
          <Container maxWidth="lg">
            <SplitHeading
              eyebrow="Why LawnHub"
              title="Everything a lawn care business runs on."
              body={
                <>
                  No add-ons, no upgrade tiers.{' '}
                  <MuiLink component={RouterLink} to="/provider-benefits" sx={{ color: GREEN, fontWeight: 600 }}>
                    See every feature
                  </MuiLink>
                  .
                </>
              }
            />
            <Grid container spacing={{ xs: 4, md: 5 }}>
              {PROVIDER_HIGHLIGHTS.map((h) => (
                <Grid item xs={12} sm={6} md={3} key={h.title}>
                  <Box sx={{ borderTop: `2px solid ${INK}`, pt: 2.5 }}>
                    <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: MINT, color: GREEN, display: 'grid', placeItems: 'center', mb: 2 }}>
                      {h.icon}
                    </Box>
                    <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.05rem', mb: 1, color: INK }}>
                      {h.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.925rem', lineHeight: 1.6, color: MUTED }}>{h.body}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        <ProviderSavingsCalculator />

        {/* How it works */}
        <Box component="section" sx={{ py: { xs: 7, md: 11 }, borderTop: `1px solid ${LINE}` }}>
          <Container maxWidth="lg">
            <SplitHeading
              eyebrow="How it works"
              title="From signup to payout."
              body={
                <>
                  No spreadsheet chaos and no forced monthly plan.{' '}
                  <MuiLink component={RouterLink} to="/how-it-works" sx={{ color: GREEN, fontWeight: 600 }}>
                    See the full walkthrough
                  </MuiLink>
                  .
                </>
              }
            />
            <Grid container spacing={{ xs: 4, md: 6 }}>
              {PROVIDER_STEPS.map((s, i) => (
                <Grid item xs={12} md={4} key={s.title}>
                  <Box sx={{ borderTop: `2px solid ${INK}`, pt: 2.5 }}>
                    <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: GREEN, fontVariantNumeric: 'tabular-nums', mb: 1.5 }}>
                      {String(i + 1).padStart(2, '0')}
                    </Typography>
                    <Typography component="h3" sx={{ fontWeight: 700, fontSize: '1.2rem', color: INK, mb: 1 }}>
                      {s.title}
                    </Typography>
                    <Typography sx={{ fontSize: '0.95rem', lineHeight: 1.65, color: MUTED }}>{s.body}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* Pricing */}
        <Box component="section" sx={{ bgcolor: DEEP, color: '#fff', py: { xs: 8, md: 10 } }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 4, md: 6 }} alignItems="center">
              <Grid item xs={12} md={5}>
                <Eyebrow light>Pricing</Eyebrow>
                <SectionTitle light>You pay when the work gets done.</SectionTitle>
                <Lede light>
                  A small platform fee on completed jobs. Card processing is paid by the customer, not taken from your payout.{' '}
                  <MuiLink component={RouterLink} to="/provider-benefits" sx={{ color: '#fff', fontWeight: 600 }}>
                    Full pricing details
                  </MuiLink>
                  .
                </Lede>
              </Grid>
              <Grid item xs={12} md={7}>
                <Grid container>
                  {FEES.map((f, i) => (
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      key={f.value + f.label}
                      sx={{
                        py: { xs: 2.5, sm: 1 },
                        pl: { sm: i === 0 ? 0 : 3 },
                        pr: { sm: 2 },
                        borderLeft: { sm: i === 0 ? 'none' : `1px solid ${LINE_ON_DARK}` },
                        borderTop: { xs: i === 0 ? 'none' : `1px solid ${LINE_ON_DARK}`, sm: 'none' },
                      }}
                    >
                      <Typography sx={{ fontSize: { xs: '2.75rem', md: '3.5rem' }, fontWeight: 800, letterSpacing: '-0.04em', lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
                        {f.value}
                      </Typography>
                      <Typography sx={{ mt: 1, fontSize: '0.9rem', color: 'rgba(255,255,255,0.72)' }}>{f.label}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Grid>
            </Grid>
          </Container>
        </Box>

        <ClosingCta
          title="Ready to run your business on LawnHub?"
          body="Create a provider account in a few minutes, or walk through the interactive demo first."
          primary={{ label: 'Create a free provider account', onClick: () => navigate('/register', { state: { userType: 'provider' } }) }}
          secondary={{ label: 'Request a demo', to: '/request-demo' }}
        />

        {/* Footer */}
        <Box component="footer" sx={{ bgcolor: '#0f2416', color: 'rgba(255,255,255,0.72)', pt: { xs: 6, md: 8 }, pb: 4 }}>
          <Container maxWidth="lg">
            <Grid container spacing={{ xs: 4, md: 6 }}>
              <Grid item xs={12} md={4}>
                <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: '1.25rem', letterSpacing: '-0.01em', mb: 1 }}>LawnHub</Typography>
                <Typography sx={{ fontSize: '0.9rem', lineHeight: 1.6, maxWidth: 300, mb: 2.5 }}>
                  Business software and a customer marketplace for lawn care and snow removal companies.
                </Typography>
                <Typography sx={{ fontSize: '0.9rem' }}>Serving providers nationwide</Typography>
                <MuiLink href="tel:+18883127995" sx={{ display: 'inline-block', mt: 0.5, color: '#fff', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none' }}>
                  (888) 312-7995
                </MuiLink>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 2, ml: -1 }}>
                  <IconButton
                    component="a"
                    href="https://www.facebook.com/profile.php?id=61581967089313"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Facebook"
                    sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
                  >
                    <Facebook />
                  </IconButton>
                  <IconButton
                    component="a"
                    href="https://www.instagram.com/illinoislawnhub/"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label="Instagram"
                    sx={{ color: 'rgba(255,255,255,0.6)', '&:hover': { color: '#fff' } }}
                  >
                    <Instagram />
                  </IconButton>
                </Box>
              </Grid>
              {FOOTER_COLUMNS.map((col) => (
                <Grid item xs={6} sm={4} md={col.title === 'Account' ? 2 : 3} key={col.title}>
                  <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '0.8rem', letterSpacing: '0.12em', textTransform: 'uppercase', mb: 2 }}>
                    {col.title}
                  </Typography>
                  <Stack spacing={1.25}>
                    {col.links.map((l) => (
                      <MuiLink
                        key={l.to}
                        component={RouterLink}
                        to={l.to}
                        sx={{ color: 'rgba(255,255,255,0.72)', fontSize: '0.9rem', textDecoration: 'none', '&:hover': { color: '#fff' } }}
                      >
                        {l.label}
                      </MuiLink>
                    ))}
                  </Stack>
                </Grid>
              ))}
            </Grid>
            <Box sx={{ mt: { xs: 5, md: 7 }, pt: 3, borderTop: '1px solid rgba(255,255,255,0.12)' }}>
              <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)' }}>
                © {new Date().getFullYear()} LawnHub LLC. All rights reserved.
              </Typography>
            </Box>
          </Container>
        </Box>
      </MarketingPage>
    </>
  );
};

export default HomePage;
